import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Modal,
  Space,
  Spin,
  Tag,
  Timeline,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  UndoOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";

const { Text, Paragraph } = Typography;

/**
 * Panel de "Cierres del auditor (submissions)" — pensado para el
 * admin que valida la auditoría.
 *
 * Muestra la lista cronológica descendente de submissions con:
 *   · Fecha + autor.
 *   · Estado (activa / devuelta) y motivo de devolución si aplica.
 *   · Botón "Ver snapshot" que abre un modal con el JSON congelado.
 *
 * El snapshot es la prueba histórica de lo que el auditor entregó;
 * si más adelante hay reclamo, el admin puede volver a este panel y
 * ver exactamente qué había en el momento del cierre.
 *
 * `refreshKey` permite al padre forzar un re-fetch tras finalizar,
 * devolver o reabrir.
 */

interface SubmissionActor {
  id: number | null;
  username: string | null;
}

interface Submission {
  id: string;
  submitted_at: string;
  submitted_by: SubmissionActor;
  returned_at: string | null;
  returned_by: SubmissionActor | null;
  return_reason: string;
  is_void: boolean;
  snapshot_json: Record<string, unknown>;
}

interface Props {
  auditId: number;
  /** Bump para forzar re-fetch tras submit/reopen/return/finalize. */
  refreshKey?: number;
  /** Cuando true, oculta el panel si no hay submissions (útil para
   *  esconderlo por completo en auditorías que aún no se cerraron
   *  nunca). Por defecto muestra un `Empty`. */
  hideWhenEmpty?: boolean;
}

const fmtDateTime = (iso: string): string =>
  dayjs(iso).format("DD/MM/YYYY HH:mm");

export const AuditSubmissionsPanel = ({
  auditId,
  refreshKey = 0,
  hideWhenEmpty = false,
}: Props) => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const fetchSubmissions = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get<{ submissions: Submission[] }>(
        `/audits/${auditId}/submissions/`,
      );
      setSubmissions(data?.submissions ?? []);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ?? err?.message ?? "Error";
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    void fetchSubmissions();
  }, [fetchSubmissions, refreshKey]);

  const currentSubmission = useMemo(
    () => submissions.find((s) => s.id === openId) ?? null,
    [submissions, openId],
  );

  if (hideWhenEmpty && !loading && submissions.length === 0 && !error) {
    return null;
  }

  return (
    <Card
      size="small"
      title={
        <Space>
          <FileTextOutlined />
          <span>Cierres del auditor</span>
          {submissions.length > 0 && (
            <Tag color="blue">{submissions.length}</Tag>
          )}
        </Space>
      }
      style={{ marginBottom: 12 }}
    >
      {loading && submissions.length === 0 ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={error} />
      ) : submissions.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="El auditor aún no ha cerrado la auditoría."
        />
      ) : (
        <Timeline
          items={submissions.map((s, idx) => {
            // Estado visual: activa (verde) / devuelta (rojo) /
            // superada (gris — hay una más nueva encima).
            const isActive = idx === 0 && !s.returned_at && !s.is_void;
            const isReturned = !!s.returned_at;
            const color = isActive
              ? "green"
              : isReturned
              ? "red"
              : "gray";
            return {
              color,
              dot: isReturned ? (
                <UndoOutlined style={{ color: "#cf1322" }} />
              ) : isActive ? (
                <CheckCircleOutlined style={{ color: "#0a8a18" }} />
              ) : undefined,
              children: (
                <div>
                  <div style={{ marginBottom: 4 }}>
                    <Text strong>{fmtDateTime(s.submitted_at)}</Text>
                    {s.submitted_by?.username && (
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        · {s.submitted_by.username}
                      </Text>
                    )}
                  </div>
                  <Space size={6} wrap style={{ marginBottom: 6 }}>
                    {isActive && (
                      <Tag color="green">Cierre activo · pendiente revisión</Tag>
                    )}
                    {isReturned && (
                      <Tag color="red" icon={<WarningOutlined />}>
                        Devuelta el {fmtDateTime(s.returned_at!)}
                        {s.returned_by?.username &&
                          ` por ${s.returned_by.username}`}
                      </Tag>
                    )}
                    {!isActive && !isReturned && (
                      <Tag>Cierre superado por uno más reciente</Tag>
                    )}
                  </Space>
                  {isReturned && s.return_reason && (
                    <div
                      style={{
                        background: "#fff1f0",
                        border: "1px solid #ffa39e",
                        borderRadius: 6,
                        padding: "6px 10px",
                        marginBottom: 6,
                      }}
                    >
                      <Text style={{ fontSize: 12, color: "#a8071a" }}>
                        <strong>Motivo:</strong> {s.return_reason}
                      </Text>
                    </div>
                  )}
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={() => setOpenId(s.id)}
                  >
                    Ver snapshot
                  </Button>
                </div>
              ),
            };
          })}
        />
      )}

      <Modal
        open={openId !== null}
        onCancel={() => setOpenId(null)}
        footer={null}
        width={860}
        title={
          currentSubmission ? (
            <Space>
              <FileTextOutlined />
              <span>
                Snapshot del cierre —{" "}
                {fmtDateTime(currentSubmission.submitted_at)}
              </span>
            </Space>
          ) : (
            "Snapshot del cierre"
          )
        }
        destroyOnClose
      >
        {currentSubmission && (
          <SnapshotViewer snapshot={currentSubmission.snapshot_json} />
        )}
      </Modal>
    </Card>
  );
};

// ────────────────────────────────────────────────────────────────
// Snapshot viewer
// ────────────────────────────────────────────────────────────────

/**
 * Renderiza el JSON del snapshot de forma legible por bloques:
 * datos generales, parcelas, control points, adjacent crops,
 * samples, firmas. Recuerda que el JSON es opaco a nivel API —
 * mostramos los campos "clave" y ofrecemos un "Ver JSON crudo"
 * como fallback para casos que no cuadren con la vista prettified.
 */
const SnapshotViewer = ({
  snapshot,
}: {
  snapshot: Record<string, any>;
}) => {
  const [rawOpen, setRawOpen] = useState(false);

  const audit = (snapshot.audit ?? {}) as Record<string, any>;
  const producer = (snapshot.producer ?? {}) as Record<string, any>;
  const farm = (snapshot.farm ?? {}) as Record<string, any>;
  const auditor = (snapshot.auditor ?? {}) as Record<string, any>;
  const plots = (snapshot.audit_plots ?? []) as any[];
  const cps = (snapshot.control_points ?? []) as any[];
  const acs = (snapshot.adjacent_crops ?? []) as any[];
  const samples = (snapshot.samples ?? []) as any[];
  const signatures = (snapshot.signatures ?? []) as any[];

  return (
    <div>
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        Estado congelado en el momento del cierre. Cualquier cambio
        posterior queda fuera de esta copia. Versión del formato:{" "}
        <code>{snapshot.version ?? "—"}</code>.
      </Paragraph>

      <Section title="Datos generales">
        <Field label="Referencia" value={audit.reference} />
        <Field label="Tipo" value={audit.audit_type_name} />
        <Field label="Estado" value={audit.audit_state_name} />
        <Field label="Auditor" value={auditor.name} />
        <Field label="Fecha auditoría" value={audit.start_date} />
        <Field
          label="Horario"
          value={
            audit.start_time || audit.finishing_time
              ? `${audit.start_time ?? "—"} — ${audit.finishing_time ?? "—"}`
              : "—"
          }
        />
        <Field label="Productor" value={producer.name} />
        <Field label="Finca" value={farm.name} />
        <Field label="Contacto receptor" value={audit.contact_receiver} />
        <Field
          label="Contraparte firmada el"
          value={
            audit.counterpart_signed_at
              ? dayjs(audit.counterpart_signed_at).format("DD/MM/YYYY HH:mm")
              : null
          }
        />
      </Section>

      {plots.length > 0 && (
        <Section title={`Parcelas auditadas (${plots.length})`}>
          {plots.map((p) => (
            <SubBlock key={p.id} title={p.name || `Parcela #${p.id}`}>
              <Field label="Variedad" value={p.variety_name} />
              <Field label="Patrón" value={p.pattern_name} />
              <Field
                label="Marco"
                value={
                  p.plantation_frame_row && p.plantation_frame_column
                    ? `${p.plantation_frame_row} × ${p.plantation_frame_column}`
                    : null
                }
              />
              <Field label="Superficie (ha)" value={p.surface} />
              <Field label="Nº plantas" value={p.plants} />
              <Field label="Aforo (kg)" value={p.kg_plot} />
              <Field label="Fotos" value={p.photos?.length ?? 0} />
              <Field label="Bloques" value={p.blocks?.length ?? 0} />
            </SubBlock>
          ))}
        </Section>
      )}

      {cps.length > 0 && (
        <Section title={`Puntos de control (${cps.length})`}>
          {cps.map((cp) => (
            <SubBlock key={cp.id} title={cp.name || `CP ${cp.id}`}>
              <Field label="Motivo" value={cp.reason_name} />
              <Field label="Estado" value={cp.status} />
              <Field label="Revisiones" value={cp.reviews?.length ?? 0} />
            </SubBlock>
          ))}
        </Section>
      )}

      {acs.length > 0 && (
        <Section title={`Cultivos colindantes (${acs.length})`}>
          {acs.map((ac) => (
            <SubBlock key={ac.id} title={ac.name}>
              <Field label="Especie" value={ac.species_name} />
              <Field label="Variedad" value={ac.variety_name} />
              <Field label="Estado" value={ac.status} />
              <Field label="Revisiones" value={ac.reviews?.length ?? 0} />
            </SubBlock>
          ))}
        </Section>
      )}

      {samples.length > 0 && (
        <Section title={`Muestras (${samples.length})`}>
          {samples.map((s) => (
            <SubBlock
              key={s.id}
              title={s.sample_reference || `Muestra ${s.id}`}
            >
              <Field label="Tipo" value={s.sample_type} />
              <Field label="Lab" value={s.lab_code} />
              <Field label="Waypoint" value={s.waypoint_name} />
            </SubBlock>
          ))}
        </Section>
      )}

      {signatures.length > 0 && (
        <Section title={`Firmas (${signatures.length})`}>
          {signatures.map((sig) => (
            <SubBlock
              key={sig.id}
              title={`${sig.signer_type} · ${sig.signer_name}`}
            >
              <Field label="Cargo" value={sig.signer_position_name} />
              <Field label="DNI" value={sig.signer_nif} />
              <Field
                label="Firmada el"
                value={
                  sig.signed_at
                    ? dayjs(sig.signed_at).format("DD/MM/YYYY HH:mm")
                    : null
                }
              />
              {sig.is_partial_at_signing && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="orange">Firma sobre acta parcial</Tag>
                </div>
              )}
            </SubBlock>
          ))}
        </Section>
      )}

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Button
          size="small"
          type="link"
          onClick={() => setRawOpen((v) => !v)}
        >
          {rawOpen ? "Ocultar JSON crudo" : "Ver JSON crudo"}
        </Button>
      </div>
      {rawOpen && (
        <pre
          style={{
            background: "#0f0f10",
            color: "#e6e6e6",
            padding: 12,
            borderRadius: 6,
            fontSize: 11,
            lineHeight: 1.4,
            maxHeight: 380,
            overflow: "auto",
          }}
          onClick={(e) => {
            const sel = window.getSelection();
            if (sel) {
              const range = document.createRange();
              range.selectNodeContents(e.currentTarget);
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }}
        >
          {JSON.stringify(snapshot, null, 2)}
        </pre>
      )}
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div
    style={{
      marginBottom: 14,
      background: "#fafafa",
      border: "1px solid #f0f0f0",
      borderRadius: 6,
      padding: "10px 12px",
    }}
  >
    <div
      style={{
        fontWeight: 600,
        fontSize: 13,
        marginBottom: 8,
        color: "#1f1f1f",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const SubBlock = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div
    style={{
      padding: "6px 8px",
      background: "#ffffff",
      border: "1px solid #f0f0f0",
      borderRadius: 4,
      marginBottom: 6,
    }}
  >
    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
      {title}
    </div>
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: 4,
      }}
    >
      {children}
    </div>
  </div>
);

const Field = ({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) => {
  const display =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div style={{ fontSize: 12 }}>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {label}
      </Text>
      <div style={{ fontWeight: 500 }}>{display}</div>
    </div>
  );
};

export default AuditSubmissionsPanel;
